export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function createCopy(object) {
  return JSON.parse(JSON.stringify(object))
}

export function downloadCSV(data, fileName, options = {}) {
  const {
    delimiter = ",",
    headerKeys = [],
    excludedKeys = [],
    dataFormatter = {}, // e.g. { time_gmt: (value) => moment(value).format("MM-DD-YYYY HH:mm") }
    headerFormatter = {}, // e.g. { nen3610id: "code" }
    columnOrder = [], // e.g. ["id", "code", "objecttype", "rating", "summary"]
  } = options

  if (!Array.isArray(data) || data.length === 0) {
    console.warn("downloadCSV: No data provided.")
    return
  }

  // ---- Determine which headers to use ----
  let header = Object.keys(data[0])

  if (headerKeys.length > 0) {
    // use only specific headers (ignore excludedKeys)
    header = headerKeys.filter(h => header.includes(h))
  } else if (excludedKeys.length > 0) {
    // exclude certain keys
    header = header.filter(h => !excludedKeys.includes(h))
  }

  // ---- Apply columnOrder if provided ----
  if (columnOrder.length > 0) {
    const lowerColumnOrder = columnOrder.map(c => c.toLowerCase())

    // Match headers in the desired order, case-insensitively, preserving original casing
    const orderedHeaders = []
    const remainingHeaders = [...header]

    lowerColumnOrder.forEach(col => {
      const matchIndex = remainingHeaders.findIndex(h => h.toLowerCase() === col)
      if (matchIndex !== -1) {
        orderedHeaders.push(remainingHeaders[matchIndex])
        remainingHeaders.splice(matchIndex, 1)
      }
    })

    // Append the rest of the columns that weren't specified
    header = [...orderedHeaders, ...remainingHeaders]
  }

  // ---- Escape and quote CSV values ----
  const escapeCSV = (value) => {
    if (value === null || value === undefined) return ""
    let str = String(value).replace(/"/g, '""')
    if (str.includes(delimiter) || str.includes("\n") || str.includes('"')) {
      str = `"${str}"`
    }
    return str
  }

  // ---- Build header row ----
  const headerRow = header.map(h => escapeCSV(headerFormatter[h] || h)).join(delimiter)

  // ---- Build data rows ----
  const dataRows = data.map(row =>
    header.map(fieldName => {
      const value = row[fieldName]
      // Apply data formatter if available
      const formattedValue =
        typeof dataFormatter[fieldName] === "function"
          ? dataFormatter[fieldName](value, row)
          : value
      return escapeCSV(formattedValue)
    }).join(delimiter)
  )

  // ---- Compose CSV content ----
  const csvLines = [
    `sep=${delimiter}`,
    headerRow,
    ...dataRows
  ]

  const csvContent = "\uFEFF" + csvLines.join("\r\n")

  // ---- Trigger download ----
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = fileName.includes(".") ? fileName : `${fileName}.csv`

  document.body.appendChild(link)
  link.click()

  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function downloadData(content, fileName, options = {}) {
  const {
    type = "application/json" // e.g. { type: "text/plain;charset=utf-8" }
  } = options

  let blob
  if (options.type === "blob") {
    blob = content
  } else {
    blob = new Blob([content], { type: type })
  }

  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = fileName.includes(".") ? fileName : `${fileName}.json`

  document.body.appendChild(link)
  link.click()

  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

const EARTH_RADIUS_M = 6371008.8

// Afstand in meters tussen twee punten over het aardoppervlak (haversine).
//
// Niet de platte stelling van Pythagoras op graden: een graad lengte is bij de evenaar ruim
// 111 km en bij 60° nog maar de helft daarvan, dus "dichtstbijzijnde" zou zonder correctie op
// hogere breedtegraden de verkeerde locatie aanwijzen.
export function distanceInMeters(a, b) {
  if (!a || !b) return null
  if (a.lat == null || a.lng == null || b.lat == null || b.lng == null) return null

  const toRad = (deg) => (deg * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)

  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

/**
 * De dichtstbijzijnde duiklocatie bij een punt.
 *
 * `maxDistanceM` is optioneel. Zonder grens is er altijd een uitkomst zolang de lijst gevuld is,
 * dat is wat het formulier wil: een klik kiest de dichtstbijzijnde, hoe ver die ook ligt, en de
 * afstand komt in beeld zodat de melder zelf kan zien of die keuze ergens op slaat. Met een grens
 * krijg je `null` als niets binnen bereik ligt.
 *
 * Locaties zonder coördinaten doen niet mee, die zijn per definitie nergens dichtbij.
 *
 * Lineair over de hele lijst: bij ruim 8000 locaties is dat een paar honderd microseconden per
 * klik, en dat weegt niet op tegen een index die bijgehouden moet worden.
 */
export function nearestDiveSite(point, diveSites, maxDistanceM) {
  if (!point) return null

  let best = null

  for (const site of diveSites || []) {
    const distance = distanceInMeters(point, { lat: site.decimal_latitude, lng: site.decimal_longitude })
    if (distance === null) continue
    if (maxDistanceM != null && distance > maxDistanceM) continue
    if (!best || distance < best.distance) best = { site, distance }
  }

  return best
}

// Roomy bounding box around Bonaire and its surrounding waters, adjust here if it needs to be tighter/wider.
export const BONAIRE_BBOX = { minLat: 11.95, maxLat: 12.35, minLng: -68.50, maxLng: -68.15 }

export function isWithinBbox(location, bbox) {
  if (!location || location.lat === null || location.lat === undefined || location.lng === null || location.lng === undefined) return false

  return location.lat >= bbox.minLat && location.lat <= bbox.maxLat
    && location.lng >= bbox.minLng && location.lng <= bbox.maxLng
}

/**
 * De waarde van een numeriek invoerveld, begrensd: nooit onder `min` (standaard 0) en, als die er
 * is, niet boven `max`. Een leeg veld blijft leeg (`null`), net als iets wat geen getal is.
 *
 * Voor de formulieren van waarnemingen en tanks: een aantal, een diepte of een temperatuur in een
 * aquarium kan niet negatief zijn. Coördinaten wel, dus die gaan hier niet doorheen.
 */
export function clampNumberInput(raw, { min = 0, max } = {}) {
  if (raw === "" || raw === null || raw === undefined) return null

  const value = Number(raw)
  if (!Number.isFinite(value)) return null

  const atLeast = Math.max(value, min)
  return typeof max === "number" ? Math.min(atLeast, max) : atLeast
}

/**
 * Voor `onKeyDown` van een getalveld dat niet negatief mag worden: het minteken komt er niet in.
 * Zonder dit typt iemand "-5" en ziet hij 0 verschijnen, wat leest als een fout in het veld.
 */
export function blockMinusKey(event) {
  if (event.key === "-" || event.key === "Subtract") event.preventDefault()
}

export function isNullOrEmpty(value) {
  return value === null || value === undefined || value.toString().trim() === "";
}

export function isValidHttpUrl(string) {
  let url

  try {
    url = new URL(string)
  } catch (_) {
    return false
  }

  return url.protocol === "http:" || url.protocol === "https:"
}

// Het antwoord van een request die is afgebroken. Gedeeld met `catchMessage`, die er niets van
// meldt; het staat als constante en niet als losse tekst zodat de twee kanten niet uit elkaar lopen.
export const REQUEST_ABORTED = "Request aborted"

export async function makeRequest(url, functionName, method, signal, token, parameters, body, requestHeaders, responseType) {
  return await new Promise(function (resolve, reject) {
    const xhr = new XMLHttpRequest()

    if ("withCredentials" in xhr) {
      xhr.open(method, url, true)

      token && xhr.setRequestHeader("Authorization", `Bearer ${token}`)
      xhr.setRequestHeader("Function", functionName)
      xhr.setRequestHeader("Parameters", JSON.stringify(parameters))

      if (requestHeaders) {
        Object.entries(requestHeaders).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value)
        })
      }
      if (responseType) {
        xhr.responseType = responseType
      }
    }

    xhr.onload = function () {
      if (this.status >= 200 && this.status < 300) {
        resolve(xhr.response)
      } else {
        // `status` erbij voor wie een bepaalde fout anders wil afhandelen, zoals de 409 van een
        // hertraining die al loopt. Wie alleen naar `exception` kijkt merkt er niets van.
        resolve({ exception: xhr.response, status: this.status })
      }
    }

    xhr.onerror = function () {
      resolve({ exception: xhr.response })
    }

    xhr.onabort = function () {
      // Blijft een `exception`, zodat elke aanroeper net als voorheen afbreekt en niets teruggeeft.
      // Het merkteken is er zodat `catchMessage` hem niet aan de gebruiker toont: afbreken doen wij
      // zelf, bij het verlaten van een pagina, en dat is geen fout om te melden.
      resolve({ exception: REQUEST_ABORTED })
    }

    xhr.withCredentials = false

    if (body instanceof FormData) {
      xhr.send(body)
    } else {
      xhr.send(JSON.stringify(body))
    }

    if (signal) {
      signal.addEventListener("abort", function () {
        if (signal.aborted) {
          xhr.abort()
        }
      })
    }
  })
}

export function scrollToElement(id, offset = 0, behavior = "smooth") {
  const element = document.getElementById(id)

  if (element) {
    const rect = element.getBoundingClientRect()

    if (rect) {
      window.scrollTo({
        top: rect.top + window.scrollY + offset,
        behavior: behavior
      })
    }
  }
}

export function sortByKey(array, key, desc) {
  const sorted = array.slice().sort((a, b) => {
    const x = a[key]
    const y = b[key]

    // Handle undefined, null, or empty values, push them to the bottom
    if (x == null || x === "") return 1  // a goes after b
    if (y == null || y === "") return -1 // b goes after a

    // Normal comparison
    if (x < y) return -1
    if (x > y) return 1
    return 0
  })

  if (desc === "desc") return sorted.reverse()
  return sorted
}

export function sortByKeys(array, keys) {
  // keys: [{ key: string, dir: "asc" | "desc" }, ...]
  return array.slice().sort((a, b) => {
    for (const { key, dir } of keys) {
      const x = a[key]
      const y = b[key]

      // Push null/empty to the bottom regardless of direction
      if ((x == null || x === "") && (y == null || y === "")) continue
      if (x == null || x === "") return 1
      if (y == null || y === "") return -1

      let cmp = 0
      if (x < y) cmp = -1
      else if (x > y) cmp = 1

      if (cmp !== 0) return dir === "desc" ? -cmp : cmp
    }
    return 0
  })
}

// Bestandsgrootte als leesbare tekst. Binaire stappen (1024), want dat is wat een browser voor
// `File.size` teruggeeft.
export function formatFileSize(bytes) {
  if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes < 0) return null
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`
}
